import React from 'react'
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl,
} from 'react-intl'
import { Section, SectionTitle } from '../../../shared/section'
import { SectionHero } from '../../../shared/section/SectionHero'
import { Activity, Category, Group, Progress, Type } from '../types'
import { SectionItemContentWithIcon } from '../../../shared/section/SectionItemContentWithIcon'
import { IconEvent } from '../../../shared/svgs'
import { getDurationAsString } from '../form/dateFormatters'
import { translationsActivity } from '../translations/translationsActivity'

const ConfigItems: React.FC<{ type: string; data: string | undefined }> = ({
  type,
  data,
}) => {
  return data ? (
    <div>
      <span className="font-bold">{type}: </span> {data}
    </div>
  ) : (
    <div>
      <span className="font-bold">{type}: </span> -
    </div>
  )
}

const ActivityDate: React.FC<{
  date: string | null
  lable: string
  dateHidden: boolean
  timeHidden: boolean
}> = ({ date, lable, dateHidden, timeHidden }) => {
  return date ? (
    <SectionItemContentWithIcon icon={IconEvent}>
      <span className="font-bold">{lable}</span>:{' '}
      {!dateHidden && <FormattedDate value={new Date(date)} />}
      {!dateHidden && !timeHidden && ' - '}
      {!timeHidden && <FormattedTime value={new Date(date)} />}
    </SectionItemContentWithIcon>
  ) : null
}

const allFieldsHidden = (activityGroup: Group): boolean =>
  activityGroup.fields.startDate.dateHidden &&
  activityGroup.fields.startDate.timeHidden &&
  activityGroup.fields.endDate.dateHidden &&
  activityGroup.fields.endDate.timeHidden &&
  activityGroup.fields.dueDate.dateHidden &&
  activityGroup.fields.dueDate.timeHidden &&
  activityGroup.fields.activityCategoryId.hidden &&
  activityGroup.fields.activityTypeId.hidden &&
  activityGroup.fields.activityProgressId.hidden

const ActivityDetailHero: React.FC<{
  activity: Activity
  categories: Array<Category>
  activityGroup: Group
}> = ({ activity, categories, activityGroup }) => {
  const intl = useIntl()
  let category: Category | undefined
  let type: Type | undefined
  let progress: Progress | undefined

  if (allFieldsHidden(activityGroup)) {
    return null
  }

  const hideStartDate =
    activityGroup.fields.startDate.dateHidden &&
    activityGroup.fields.startDate.timeHidden
  const hideEndDate =
    activityGroup.fields.endDate.dateHidden &&
    activityGroup.fields.endDate.timeHidden

  category = categories.find(
    (category) => category.id === activity.activityCategoryId
  )
  type = category?.types.find((type) => type.id === activity.activityTypeId)
  progress = category?.progresses.find(
    (progress) => progress.id === activity.activityProgressId
  )
  //To doo
  return (
    <Section>
      <SectionTitle>
        <FormattedMessage {...translationsActivity.detailTitle} />
      </SectionTitle>
      <SectionHero>
        <div>
          <h2 className="text-3xl">{activity.name}</h2>
        </div>
        <div className="flex flex-wrap overflow-hidden">
          <div className="w-full space-y-5 overflow-hidden lg:w-1/2">
            {!hideStartDate && (
              <ActivityDate
                date={activity.startDate}
                lable={intl.formatMessage({
                  id: 'activity.detail.startDate',
                  defaultMessage: 'Start date',
                })}
                dateHidden={activityGroup.fields.startDate.dateHidden}
                timeHidden={activityGroup.fields.startDate.timeHidden}
              />
            )}
            {!hideEndDate && (
              <ActivityDate
                date={activity.endDate}
                lable={intl.formatMessage({
                  id: 'activity.detail.endDate',
                  defaultMessage: 'End date',
                })}
                dateHidden={activityGroup.fields.endDate.dateHidden}
                timeHidden={activityGroup.fields.endDate.timeHidden}
              />
            )}
            {!activityGroup.fields.dueDate.dateHidden && (
              <ActivityDate
                date={activity.dueDate}
                lable={intl.formatMessage({
                  id: 'activity.detail.dueDate',
                  defaultMessage: 'Due date',
                })}
                dateHidden={activityGroup.fields.dueDate.dateHidden}
                timeHidden={activityGroup.fields.dueDate.timeHidden}
              />
            )}
            {!hideStartDate ||
              (!hideEndDate && (
                <SectionItemContentWithIcon icon={IconEvent}>
                  <span className="font-bold">
                    <FormattedMessage
                      {...translationsActivity.detailDateDuration}
                    />
                  </span>
                  : {getDurationAsString(activity.startDate, activity.endDate)}
                </SectionItemContentWithIcon>
              ))}
            {!activityGroup.fields.billable.hidden && (
              <SectionItemContentWithIcon icon={IconEvent}>
                <span className="font-bold">
                  <FormattedMessage {...translationsActivity.detailBillable} />
                </span>
                :{' '}
                {activity.billable ? (
                  <FormattedMessage
                    id="activity.detail.billable.yes"
                    defaultMessage="Yes"
                  />
                ) : (
                  <FormattedMessage
                    id="activity.detail.billable.no"
                    defaultMessage="No"
                  />
                )}
              </SectionItemContentWithIcon>
            )}
          </div>
          <div className="mt-10 w-full space-y-5 overflow-hidden lg:mt-0 lg:w-1/2">
            {!activityGroup.fields.activityCategoryId.hidden && (
              <ConfigItems
                type={intl.formatMessage(
                  translationsActivity.detailHeroCategory
                )}
                data={category?.name}
              />
            )}
            {!activityGroup.fields.activityTypeId.hidden && (
              <ConfigItems
                type={intl.formatMessage(translationsActivity.detailHeroType)}
                data={type?.name}
              />
            )}
            {!activityGroup.fields.activityProgressId.hidden && (
              <ConfigItems
                type={intl.formatMessage(
                  translationsActivity.detailHeroProgress
                )}
                data={progress?.name}
              />
            )}
          </div>
        </div>
      </SectionHero>
    </Section>
  )
}

export { ActivityDetailHero }
